import React, { useState, useEffect } from 'react';
import { uploadService, PreviewData } from '../services/uploadService';

interface PDFPreviewModalProps {
  uploadId: string;
  isOpen: boolean;
  onClose: () => void;
  onApprove?: () => void; // Optional - only show import button if provided
}

type ViewMode = 'text' | 'tables' | 'insights';

const PDFPreviewModal: React.FC<PDFPreviewModalProps> = ({
  uploadId,
  isOpen,
  onClose,
  onApprove
}) => {
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ViewMode>('text');
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (isOpen && uploadId) {
      fetchPDFPreview();
    }
  }, [isOpen, uploadId]);

  const fetchPDFPreview = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch the preview data which includes page extractions
      const data = await uploadService.getPreview(uploadId);
      setPreviewData(data);
    } catch (err) {
      console.error('Failed to fetch PDF preview:', err);
      setError('Failed to load PDF preview. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setApproving(true);
      // Trigger the import/ingestion process
      await uploadService.startIngestion(uploadId);
      onApprove?.();
    } catch (err) {
      console.error('Failed to approve PDF import:', err);
      setError('Failed to start import. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  const formatTableAsASCII = (data: any[]): string => {
    if (!data || data.length === 0) return 'No table data';
    
    const headers = Object.keys(data[0]);
    const colWidths = headers.map(header => {
      const maxWidth = Math.max(
        header.length,
        ...data.map(row => String(row[header] || '').length)
      );
      return Math.min(maxWidth, 30); // Cap at 30 chars
    });

    // Build header row
    let table = '| ';
    headers.forEach((header, i) => {
      table += header.padEnd(colWidths[i]) + ' | ';
    });
    table += '\n';

    // Build separator
    table += '|';
    colWidths.forEach(width => {
      table += '-'.repeat(width + 2) + '|';
    });
    table += '\n';

    // Build data rows
    data.forEach(row => {
      table += '| ';
      headers.forEach((header, i) => {
        const value = String(row[header] || '').substring(0, colWidths[i]);
        table += value.padEnd(colWidths[i]) + ' | ';
      });
      table += '\n';
    });

    return table;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">
              📄 PDF Document Review
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {previewData?.filename || 'Loading...'} • {previewData?.metadata?.page_count || 0} pages
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('text')}
            className={`px-6 py-3 font-medium text-sm border-b-2 ${
              activeTab === 'text'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📝 Extracted Text
          </button>
          <button
            onClick={() => setActiveTab('tables')}
            className={`px-6 py-3 font-medium text-sm border-b-2 ${
              activeTab === 'tables'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📊 Tables ({previewData?.summary?.detected_tables ?? 0})
          </button>
          <button
            onClick={() => setActiveTab('insights')}
            className={`px-6 py-3 font-medium text-sm border-b-2 ${
              activeTab === 'insights'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🔍 Extraction Insights
          </button>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600 mb-4"></div>
                <div className="text-gray-600">Loading PDF content...</div>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-red-600 mb-4">
                  <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {error}
                </div>
                <button 
                  onClick={fetchPDFPreview}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : previewData ? (
            <>
              {/* Text Tab */}
              {activeTab === 'text' && (
                <div className="h-full overflow-y-auto p-6">
                  <div className="space-y-6">
                    {previewData.page_extractions?.map((page, index) => (
                      <div key={index}>
                        <h3 className="text-sm font-medium text-gray-900 mb-3">
                          Page {page.page_number}
                        </h3>
                        {page.raw_text ? (
                          <pre className="whitespace-pre-wrap text-sm font-mono text-gray-700 leading-relaxed bg-white p-4 rounded-lg border border-gray-200">
                            {page.raw_text}
                          </pre>
                        ) : (
                          <div className="text-gray-500 italic bg-gray-100 p-4 rounded-lg">
                            No text extracted from this page
                          </div>
                        )}
                      </div>
                    ))}
                    {(!previewData.page_extractions || previewData.page_extractions.length === 0) && (
                      <div className="text-center py-8 text-gray-500">
                        No text content extracted from this PDF
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tables Tab */}
              {activeTab === 'tables' && (
                <div className="h-full overflow-y-auto p-6">
                  <div className="space-y-6">
                    {previewData.page_extractions?.filter(page => page.structured_data && page.structured_data.length > 0).map((page, index) => (
                      <div key={index}>
                        <h3 className="text-sm font-medium text-gray-900 mb-3">
                          Table from Page {page.page_number}
                        </h3>
                        <div className="bg-white p-4 rounded-lg border border-gray-200 overflow-x-auto">
                          <pre className="text-sm font-mono text-gray-700">{formatTableAsASCII(page.structured_data || [])}</pre>
                        </div>
                      </div>
                    ))}
                    {!previewData.page_extractions?.some(page => page.structured_data && page.structured_data.length > 0) && (
                      <div className="text-center py-8 text-gray-500">
                        No tables detected in this PDF
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Insights Tab */}
              {activeTab === 'insights' && (
                <div className="h-full overflow-y-auto p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Document Information</h3>
                      <dl className="space-y-3">
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Document Type</dt>
                          <dd className="text-sm text-gray-900">{previewData.metadata?.document_type || 'Unknown'}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Total Pages</dt>
                          <dd className="text-sm text-gray-900">{previewData.metadata?.page_count || 0}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Extraction Method</dt>
                          <dd className="text-sm text-gray-900">{previewData.metadata?.extraction_method || 'Unknown'}</dd>
                        </div>
                      </dl>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Extraction Results</h3>
                      <dl className="space-y-3">
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Text Extracted</dt>
                          <dd className="text-sm text-gray-900">{previewData.metadata?.has_text ? 'Yes' : 'No'}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Tables Detected</dt>
                          <dd className="text-sm text-gray-900">{previewData.summary?.detected_tables || 0}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Extraction Confidence</dt>
                          <dd className="text-sm text-gray-900">
                            {previewData.summary?.extraction_confidence 
                              ? `${(previewData.summary.extraction_confidence * 100).toFixed(0)}%`
                              : 'N/A'
                            }
                          </dd>
                        </div>
                      </dl>
                    </div>

                    <div className="bg-white rounded-lg border border-gray-200 p-6 md:col-span-2">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Page-by-Page Analysis</h3>
                      <div className="space-y-2">
                        {previewData.page_extractions?.map((page, index) => (
                          <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                            <span className="text-sm text-gray-600">Page {page.page_number}</span>
                            <div className="flex items-center gap-4">
                              <span className="text-sm text-gray-500">
                                {page.extraction_type} • {page.extraction_method}
                              </span>
                              <span className={`text-sm font-medium ${
                                page.confidence_score > 0.8 ? 'text-green-600' : 
                                page.confidence_score > 0.6 ? 'text-yellow-600' : 'text-red-600'
                              }`}>
                                {(page.confidence_score * 100).toFixed(0)}% confidence
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">No preview data available</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{previewData?.metadata?.page_count || 0}</span> pages
              {(previewData?.summary?.detected_tables ?? 0) > 0 && (
                <>
                  {' • '}
                  <span className="font-medium">{previewData?.summary?.detected_tables}</span> tables
                </>
              )}
              {previewData?.summary?.extraction_confidence && (
                <>
                  {' • '}
                  <span className={`font-medium ${
                    previewData.summary.extraction_confidence > 0.8 ? 'text-green-600' : 
                    previewData.summary.extraction_confidence > 0.6 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {(previewData.summary.extraction_confidence * 100).toFixed(0)}% confidence
                  </span>
                </>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {onApprove ? 'Cancel' : 'Close'}
              </button>
              {onApprove && (
                <button
                  onClick={handleApprove}
                  disabled={approving || loading || !!error}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {approving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Importing...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Approve & Import
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFPreviewModal;